/**
 * User-facing copy for insight drill-downs: a one-line plain-language
 * explainer per insight type, friendly section headings, and a period label
 * derived from the payload's window / cadence. Keeping this here means the
 * views stay free of technical jargon.
 */
import { humanize, type Rec } from "./formatInsight";

/** One-sentence explanation of what the insight compares, keyed by type. */
const INSIGHT_EXPLAINER: Record<string, string> = {
  daily_booking_summary:
    "Today's active booking portfolio, broken down by state and carrier.",
  weekly_trend: "Booking volume this week compared to the prior week.",
  portfolio_opportunity_funnel:
    "Unrealized savings and where opportunities stand this period.",
  customer_volume_trend:
    "Customers whose booking volume changed most versus the prior period.",
  early_booking_market_decline:
    "Customers that book early while market rates keep falling before departure.",
  sales_person_activity_trend:
    "Booking volume by sales person compared to the prior period.",
  rolled_delayed_disruption: "Active bookings currently tagged rolled or delayed.",
  opportunity_capture_rate:
    "How many detected savings opportunities were acted upon.",
  pricing_match_by_source_type:
    "Pricing match rate by how the freight is paid (prepaid, collect, spot).",
  newly_opened_bookings: "New bookings opened in this period and who owns them.",
  lane_volume_and_rate_trend: "Trade lanes with the biggest volume or rate moves.",
  allocation_pressure: "Trade lanes approaching their allocation limits.",
  pricing_coverage_gap: "Active bookings that still lack a matched rate.",
  spot_lane_volatility: "Spot lanes with notable rate moves against their guardrails.",
  enrichment_freshness:
    "Departing-soon bookings whose carrier updates have gone stale.",
  restriction_blocked_savings: "Savings blocked by business rules this period.",
  savings_credibility: "How predicted savings compared to what was realized.",
  optimizer_cycle_health: "Optimization run throughput and output this period.",
  agent_booking_summary:
    "This handling agent's active booking portfolio, by state, lane, and carrier.",
  agent_activity_trend:
    "This handling agent's booking volume, TEU, and rebooks versus the prior period.",
  agent_data_quality:
    "Share of this handling agent's active bookings missing key enrichment.",
};

/** Friendly headings for the `top_*` / `by_*` arrays in payloads. */
const SECTION_TITLE: Record<string, string> = {
  top_declines: "Biggest drops",
  top_increases: "Biggest gains",
  top_opportunity_holders: "Unrealized savings",
  top_customers: "Top customers",
  top_carriers: "Top carriers",
  top_sales_persons: "By sales person",
  top_lanes: "Top lanes",
  top_laggards: "Lagging customers",
  top_lane_swings: "Lane moves",
  top_unmatched_carriers: "Unmatched carriers",
  by_source_type: "By source type",
  by_rule: "By rule",
};

/** The insight/pattern discriminator key from a payload. */
export function payloadTypeKey(payload: Rec): string | undefined {
  const key = payload.insight_type ?? payload.pattern_type;
  return typeof key === "string" ? key : undefined;
}

export function explainerFor(payload: Rec): string | undefined {
  const key = payloadTypeKey(payload);
  return key ? INSIGHT_EXPLAINER[key] : undefined;
}

export function sectionTitle(key: string): string {
  return SECTION_TITLE[key] ?? humanize(key.replace(/^top_/, "").replace(/^by_/, ""));
}

/** Human "this period vs prior period" label from window_days or cadence. */
export function periodLabel(payload: Rec): string | undefined {
  const windowDays = payload.window_days;
  if (typeof windowDays === "number" && windowDays > 0) {
    return `Last ${windowDays} days vs prior ${windowDays} days`;
  }
  const period = payload.period as Rec | undefined;
  const cadence = period && typeof period.cadence === "string" ? period.cadence : undefined;
  switch (cadence) {
    case "daily":
      return "Today vs the prior day";
    case "weekly":
      return "This week vs last week";
    case "monthly":
      return "This month vs last month";
    default:
      return undefined;
  }
}
